import React from 'react';
import { AppShell, Group, Text, Button, Switch, useMantineColorScheme } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  IconLogout,
  IconSun,
  IconMoon
} from '@tabler/icons-react';

const MemberLayout = ({ children }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  const handleLogout = async () => {
    await logout();
    navigate('/member/login');
  };

  return (
    <AppShell
      header={{ height: { base: 120, sm: 100 } }}
      px={{ base: "sm", sm: 300 }}    // padding left/right
      py={{ base: "sm", sm: "md" }}     // padding top/bottom
    >

      <AppShell.Header p="md">
        {/* Mobile Layout */}
        <div
          style={{
            display: 'block',
            padding: '2px 1px',
            backgroundColor: colorScheme === 'dark' ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-white)',
          }}
          className="sm:hidden"
        >
          {/* Logo and Title Section MOBILE */}
          <div style={{
            textAlign: 'center',
            marginBottom: '8px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            flexWrap: 'wrap'
          }}>
            <img
              src="/logo512.png"
              width="36px"
              height="36px"
              style={{
                flexShrink: 0,
                borderRadius: '8px', // Rounded square for this brand
              }}
              alt="Brain Gym Logo"
            />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Text
                size="20px"
                fw={700}
                style={{
                  fontFamily: '"Bahnschrift", "Bahnschrift Condensed", "DIN Alternate", sans-serif',
                  textTransform: 'uppercase',
                  // Logic: Bright Metallic Gold for Dark Mode | Deep Bronze/Brown for Light Mode
                  
                  background: 'linear-gradient(180deg, #B8860B 0%, #8B6508 60%, #B8860B 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '0.5px',
                  lineHeight: 1,
                  
                  filter: colorScheme === 'dark' ? 'brightness(1.45) saturate(1.15)' : 'none'
                }}
              >
                BRAIN GYM
              </Text>
              <Text
                size="11px"
                fw={600}
                style={{
                  fontFamily: '"Inter", sans-serif',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: colorScheme === 'dark' ? '#E0E0E0' : '#8B6508', // Light Grey in Dark Mode, Violet in Light Mode
                  marginTop: '2px'
                }}
              >
                Brain Gym Course
              </Text>
            </div>
          </div>

          {/* Controls Section */}
          <Group
            justify="space-between"
            align="center"
            style={{ paddingTop: '4px' }}
          >
            <Switch
              size="md"
              color="violet.6"
              onLabel={<IconSun size={15} stroke={2.5} />}
              offLabel={<IconMoon size={15} stroke={2.5} />}
              checked={colorScheme === 'dark'}
              onChange={() => toggleColorScheme()}
              styles={{
                track: {
                  border: colorScheme === 'dark'
                    ? '2px solid var(--mantine-color-dark-4)'
                    : '2px solid #e9ecef'
                }
              }}
            />
            <Button
              variant="light"
              color="red"
              size="sm"
              leftSection={<IconLogout size={16} stroke={2} />}
              onClick={handleLogout}
              styles={{
                root: {
                  fontWeight: 600,
                  borderRadius: '8px',
                }
              }}
            >
              Logout
            </Button>
          </Group>
        </div>

        {/* Desktop Layout */}
        <Group
          h="80px"
          justify="space-between"
          align="center"
          style={{
            display: 'none',
            backgroundColor: colorScheme === 'dark' ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-white)',
            marginLeft: '40px',
          }}
          className="sm:flex"
        >
          {/* Left side - Logo and Title */}
          <Group align="center" gap="md">
            <img
              src="/logo512.png"
              width="55px"
              height="55px"
              style={{
                borderRadius: '10px'
              }}
              alt="Brain Gym Logo"
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text
                size="34px"
                fw={700}
                style={{
                  fontFamily: '"Bahnschrift", "Bahnschrift Condensed", "DIN Alternate", sans-serif',
                  textTransform: 'uppercase',
                  // Logic: Bright Metallic Gold for Dark Mode | Deep Bronze for Light Mode
                  background: 'linear-gradient(180deg, #B8860B 0%, #8B6508 60%, #B8860B 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '0.5px',
                  lineHeight: 0.9,
                  filter: colorScheme === 'dark' ? 'brightness(1.45) saturate(1.15)' : 'none'
              

                }}
              >
                BRAIN GYM
              </Text>
              <Text
                size="14px"
                fw={600}
                style={{
                  fontFamily: '"Inter", sans-serif',
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                  color: colorScheme === 'dark' ? '#D1D5DB' : '#8B6508', // Slate Grey vs Violet
                  marginTop: '4px'
                }}
              >
                Brain Gym Course
              </Text>
            </div>
          </Group>

          {/* Right side - Controls */}
          <Group align="center" gap="lg">
            <Switch
              size="md"
              color="violet.6"
              onLabel={<IconSun size={16} stroke={2.5} />}
              offLabel={<IconMoon size={16} stroke={2.5} />}
              checked={colorScheme === 'dark'}
              onChange={() => toggleColorScheme()}
              styles={{
                track: {
                  border: colorScheme === 'dark'
                    ? '2px solid var(--mantine-color-dark-4)'
                    : '2px solid #e9ecef'
                }
              }}
            />
            <Button
              variant="light"
              color="red"
              size="md"
              leftSection={<IconLogout size={18} stroke={2} />}
              onClick={handleLogout}
              styles={{
                root: {
                  fontWeight: 600,
                  borderRadius: '8px',
                }
              }}
            >
              Logout
            </Button>
          </Group>
        </Group>
      </AppShell.Header>



      <AppShell.Main>
        {children}
      </AppShell.Main>
    </AppShell>
  );
};

export default MemberLayout;